import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { endpoints } from '@/services/endpoints'
import { get } from '@/services/http'
import { queryKeys } from '@/services/queryClient'
import { fetchCopilotSessions, pendingTurnState } from './session'
import { textOf } from './turnReducer'

/**
 * Reading back what was already said: the list of chats, and one chat's turns.
 */

/**
 * One stored turn as the panel's message list holds it.
 *
 * Four things here are not what they look like, and all four came from reading the
 * schema rather than the field names:
 *
 * `content` is an OBJECT, never a string — `{blocks:[…]}` on an answer, `{text:…}`
 * on a question, and legitimately `{}` on a turn that produced nothing. The obvious
 * `turn.content ?? ''` renders `[object Object]` at the reader.
 *
 * `metadata_` keeps the trailing underscore ON THE WIRE. The schema declares a
 * serialization alias for the clean name and never passes `by_alias`, so the alias
 * is inert. Both are read, in this one place, so that the day the backend fixes it
 * there is a single line to delete. See BACKEND-GAPS.md.
 *
 * `status` is meaningful only on a QUESTION. A question marked `error` has no
 * answer row after it and never will, so it renders as a failed turn — otherwise it
 * reads as the copilot having ignored somebody.
 *
 * `replayed` is not cosmetic: it stops `useRevealedText` typing a months-old answer
 * out again on mount.
 */
function toMessage(turn) {
  const metadata = turn.metadata ?? turn.metadata_ ?? {}
  const content = turn.content ?? {}
  const base = { id: turn.id, role: turn.role, replayed: true }

  if (turn.role === 'user') {
    return {
      ...base,
      text: content.text ?? '',
      failed: turn.status === 'error',
    }
  }
  return {
    ...base,
    text: textOf(content.blocks ?? []),
    stages: [],
    sources: metadata.sources ?? [],
    /* What the dispatcher did is reported on the live `done` frame and stripped
       before the row is persisted, so today this is always empty on replay. Read
       anyway, objects only — the engine expects the server to collapse each to a
       bare type string one day, and a string has no module to label. */
    actions: (metadata.action ?? []).filter((a) => a && typeof a === 'object'),
    error: null,
    notice: null,
  }
}

/** The server's page ceiling. A chat longer than this replays its first 200 turns. */
const TRANSCRIPT_PAGE = 200

/**
 * One chat's turns, and whether it can take another question.
 *
 * `blocked` is the case the server will refuse: the last turn is a question with no
 * answer after it. A dead one (`error`, or reaped by the sweep) blocks the chat for
 * ever; a young `streaming` one blocks it until the engine finishes. Either way a
 * question sent now is refused, so the panel says so up front rather than letting
 * the reader type one and lose it.
 */
export async function fetchSessionTranscript(sessionId, signal) {
  const body = await get(endpoints.copilot.sessionTurns(sessionId), {
    signal,
    params: { sort_dir: 'asc', page_size: TRANSCRIPT_PAGE },
  })
  const turns = body?.turns ?? []
  const last = turns[turns.length - 1]
  const unanswered = last?.role === 'user' ? last : null
  return {
    messages: turns.map(toMessage),
    blocked: unanswered ? pendingTurnState(unanswered) : 'none',
  }
}

/**
 * Read once per chat and cached.
 *
 * `staleTime: Infinity` because the only thing that adds to an open transcript is
 * the dock itself, and the live turn is already in the panel's own state.
 * Refetching would replace a conversation in progress with the server's view of
 * it, which is behind by exactly the turn on screen.
 */
export function useSessionTranscript(sessionId) {
  return useQuery({
    queryKey: queryKeys.copilotTranscript(sessionId),
    queryFn: ({ signal }) => fetchSessionTranscript(sessionId, signal),
    enabled: Boolean(sessionId),
    staleTime: Infinity,
    retry: false,
  })
}

/** This person's chats, newest activity first, a page at a time. */
export function useCopilotSessions({ enabled = true } = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.copilotSessions,
    queryFn: ({ pageParam, signal }) => fetchCopilotSessions({ page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
    enabled,
  })
}
